import { useCallback, useEffect, useRef, useState } from 'react';
import { selectProductHierarchyMinimalHasAttemptedFetch } from 'src/store/selectors/productHierarchySelectors';
import { LDAP_GROUPS } from '../constants/appConstants';
import { isMemberOf } from '../services/api/brass/brassApiClient';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCurrentUserPermissions, setCurrentUserPermissions } from '../store/slices/accessManagementSlice';
import { fetchProductHierarchyMinimal } from '../store/slices/productHierarchySlice';
import { fetchReportsConfig, selectReportsConfigHasAttemptedFetch } from '../store/slices/reportsConfigSlice';
import { UserPermission, UserType } from '../types/admin/accessManagement.types';
import type { LoggedInUserInfo } from '../types/auth/auth.types';
import { validateEmployeeLoginWithPAPI } from '../utils/employeeServiceUtils';
import { logger } from '../utils/logger';

interface UseMembershipCheckProps {
  userAlias: string | undefined;
  isAuthInitialized: boolean;
  isServicesInitialized: boolean;
  onMembershipUpdate: (updates: Partial<LoggedInUserInfo>) => void;
  onError: (error: string) => void;
}

interface UseMembershipCheckReturn {
  isMembershipCheckComplete: boolean;
  isCriticalDataLoaded: boolean;
  checkMembership: () => Promise<void>;
  resetMembershipCheck: () => void;
}

/**
 * Custom hook to handle user membership checking, permission fetching, and critical data loading.
 *
 * This hook performs the following operations during authentication:
 * 1. Fetches the current user's permissions from the database (single API call)
 * 2. Checks LDAP group memberships (Developer and Read-Only groups)
 * 3. Applies DB-precedence override logic for developers with database records
 * 4. Updates the user's authentication details with the determined membership
 * 5. Fetches critical application data (product hierarchy and reports config)
 *
 * DB-precedence Logic:
 * - For developers with a database record, the user_type in the database takes precedence
 * - This allows developers to test the app as different user types without modifying LDAP groups
 * - Non-developer users use standard LDAP group membership
 *
 * Critical Data:
 * - Product hierarchy minimal: Required for permission checks and navigation
 * - Reports configuration: Required to determine which features to show/hide
 *
 * @remarks
 * - This hook fetches current user permissions via Redux, making them available throughout the app
 * - The permissions are stored in Redux state: `accessManagement.currentUserPermissions`
 * - Graceful error handling ensures the app remains functional even if permission checks fail
 * - Failed permission checks result in restricted access mode rather than blocking the user
 * - Uses useRef to prevent race conditions from multiple simultaneous calls
 */
export const useMembershipCheck = ({
  userAlias,
  isAuthInitialized,
  isServicesInitialized,
  onMembershipUpdate,
  onError,
}: UseMembershipCheckProps): UseMembershipCheckReturn => {
  const [isMembershipCheckComplete, setIsMembershipCheckComplete] = useState(false);
  const [isCriticalDataLoaded, setIsCriticalDataLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const isCheckingRef = useRef(false);

  // Check if critical data has been loaded
  const hasAttemptedHierarchyMinimalFetch = useAppSelector(selectProductHierarchyMinimalHasAttemptedFetch);
  const hasAttemptedReportsConfigFetch = useAppSelector(selectReportsConfigHasAttemptedFetch);

  const resetMembershipCheck = useCallback(() => {
    setIsMembershipCheckComplete(false);
    setIsCriticalDataLoaded(false);
    isCheckingRef.current = false;
  }, []);

  const checkMembership = useCallback(async (): Promise<void> => {
    // Guard against multiple calls using ref to prevent race conditions
    if (isCheckingRef.current || isMembershipCheckComplete) {
      return;
    }

    if (!userAlias || !isAuthInitialized || !isServicesInitialized) {
      setIsMembershipCheckComplete(false);
      setIsCriticalDataLoaded(false);
      return;
    }

    isCheckingRef.current = true;

    try {
      await logger.info('Starting parallel data fetching for user', { userAlias });

      // Run ALL independent API calls in parallel for optimal performance
      const [userPermissionsResult, devMembershipResult, roMembershipResult, hierarchyResult, reportsConfigResult] =
        await Promise.allSettled([
          // 1. Fetch user permissions from database
          dispatch(fetchCurrentUserPermissions(userAlias)).unwrap(),

          // 2. Check developer LDAP group membership
          isMemberOf(userAlias, LDAP_GROUPS.DEVELOPER),

          // 3. Check read-only LDAP group membership
          isMemberOf(userAlias, LDAP_GROUPS.READ_ONLY),

          // 4. Fetch product hierarchy minimal (if not already fetched)
          hasAttemptedHierarchyMinimalFetch
            ? Promise.resolve(null)
            : dispatch(fetchProductHierarchyMinimal(false)).unwrap(),

          // 5. Fetch reports configuration (if not already fetched)
          hasAttemptedReportsConfigFetch ? Promise.resolve(null) : dispatch(fetchReportsConfig({})).unwrap(),
        ]);

      await logger.info('Parallel data fetching completed', {
        userPermissions: userPermissionsResult.status,
        devMembership: devMembershipResult.status,
        roMembership: roMembershipResult.status,
        hierarchy: hierarchyResult.status,
        reportsConfig: reportsConfigResult.status,
      });

      // Process user permissions result
      let userDbRecord: UserPermission | null = null;

      if (userPermissionsResult.status === 'fulfilled' && userPermissionsResult.value) {
        userDbRecord = userPermissionsResult.value;

        // Set currentUserPermissions directly for consistency
        // This makes currentUserPermissions the true single source of truth
        dispatch(setCurrentUserPermissions(userDbRecord));

        await logger.info('User DB record loaded and set', {
          userAlias,
          userType: userDbRecord.user_type,
        });
      } else if (userPermissionsResult.status === 'rejected') {
        // User not found in DB - will use LDAP to determine access
        userDbRecord = null;
        await logger.error('Error fetching user permissions from DB', {
          error:
            userPermissionsResult.reason instanceof Error
              ? userPermissionsResult.reason.message
              : String(userPermissionsResult.reason),
        });
      } else {
        // Fulfilled but value is null - treat as not in DB
        userDbRecord = null;
        await logger.info('User not found in DB, will check LDAP membership', { userAlias });
      }

      // Process LDAP membership results
      const isDeveloperFromLdap =
        devMembershipResult.status === 'fulfilled' && devMembershipResult.value.success
          ? devMembershipResult.value.isMember
          : false;

      const isReadOnlyFromLdap =
        roMembershipResult.status === 'fulfilled' && roMembershipResult.value.success
          ? roMembershipResult.value.isMember
          : false;

      // Process critical data results
      const hierarchyLoadFailed = hierarchyResult.status === 'rejected';
      const reportsConfigLoadFailed = reportsConfigResult.status === 'rejected';

      if (hierarchyLoadFailed) {
        await logger.error('Failed to load product hierarchy minimal', {
          error:
            hierarchyResult.reason instanceof Error ? hierarchyResult.reason.message : String(hierarchyResult.reason),
        });
      }

      if (reportsConfigLoadFailed) {
        await logger.error('Failed to load reports configuration', {
          error:
            reportsConfigResult.reason instanceof Error
              ? reportsConfigResult.reason.message
              : String(reportsConfigResult.reason),
        });
      }

      // ========================================================================
      // SYNTHETIC RECORD CREATION FOR LDAP USERS WITHOUT DB RECORDS
      // ========================================================================
      // If user is in LDAP (dev or read-only) but NOT in DB, create synthetic record
      // This centralizes permissions in Redux for cleaner permission checks
      if ((isDeveloperFromLdap || isReadOnlyFromLdap) && !userDbRecord) {
        await logger.info('User in LDAP but not in DB - creating synthetic record', {
          userAlias,
          isDeveloper: isDeveloperFromLdap,
          isReadOnly: isReadOnlyFromLdap,
        });

        try {
          // Call PAPI to get user details for synthetic record
          const papiValidation = await validateEmployeeLoginWithPAPI(userAlias);

          if (papiValidation.success && papiValidation.userInfo) {
            // Determine user type based on LDAP membership
            const userType = isDeveloperFromLdap ? UserType.TECH_ADMIN : UserType.READ_ONLY;

            // Create synthetic permission record
            const syntheticPermission: UserPermission = {
              user_access_id: null, // null indicates not in DB
              user_alias: userAlias,
              is_dev: isDeveloperFromLdap,
              is_read_only: isReadOnlyFromLdap,
              user_type: userType,
              papi_info: {
                badge_color: papiValidation.userInfo.badge_color,
                business_title: papiValidation.userInfo.business_title,
                cost_center_name: papiValidation.userInfo.cost_center_name,
                employee_status: papiValidation.userInfo.employee_status,
                first_name: papiValidation.userInfo.first_name,
                job_status: papiValidation.userInfo.job_status,
                last_name: papiValidation.userInfo.last_name,
                legacy_employee_id: papiValidation.userInfo.legacy_employee_id,
                login: papiValidation.userInfo.login,
                person_id: papiValidation.userInfo.person_id,
              },
              product_lines_access: [], // Empty for full-access users
              item_metadata: {
                is_active: true,
                created_by: userAlias,
                created_at: new Date().toISOString(),
                updated_by: userAlias,
                updated_at: new Date().toISOString(),
              },
            };

            // Dispatch synthetic record ONLY to currentUserPermissions
            // We don't add to allUserPermissions (admin table) - that's for DB records only
            dispatch(setCurrentUserPermissions(syntheticPermission));

            await logger.info('Synthetic permission record created for current user', {
              userAlias,
              userType,
            });
          } else {
            // PAPI validation failed but user has LDAP - still allow access
            await logger.warn('PAPI validation failed for LDAP user, proceeding with limited info', {
              userAlias,
              validationMessage: papiValidation.validationMessage,
            });
          }
        } catch (error) {
          // Error calling PAPI - log but don't block LDAP users
          await logger.error('Error creating synthetic record, proceeding without PAPI info', {
            error: error instanceof Error ? error.message : String(error),
            userAlias,
          });
        }
      }

      // ========================================================================
      // MEMBERSHIP DETERMINATION
      // ========================================================================
      // Now simplified - only set isInitiallyDeveloper flag
      // All other permission checks use Redux userPermission as single source of truth
      const membershipUpdates: Partial<LoggedInUserInfo> = {
        isInitiallyDeveloper: isDeveloperFromLdap,
      };

      await logger.info('Membership check completed', {
        isInitiallyDeveloper: membershipUpdates.isInitiallyDeveloper,
        hasSyntheticRecord: (isDeveloperFromLdap || isReadOnlyFromLdap) && !userDbRecord,
        hasDbRecord: !!userDbRecord,
      });

      // Update user details with membership information
      onMembershipUpdate(membershipUpdates);
      setIsMembershipCheckComplete(true);

      // Mark critical data as loaded (it was fetched in parallel above)
      setIsCriticalDataLoaded(true);

      await logger.info('All data loaded successfully', {
        isInitiallyDeveloper: membershipUpdates.isInitiallyDeveloper,
        hierarchyLoaded: !hierarchyLoadFailed,
        reportsConfigLoaded: !reportsConfigLoadFailed,
      });
    } catch (error) {
      await logger.error('Error checking membership', {
        error: error instanceof Error ? error.message : String(error),
        userAlias,
      });

      // Intentionally proceed with restricted access if membership check fails
      // This ensures the app remains functional even when the Brass API is unavailable
      // Users will have minimal permissions but can still access the application
      const membershipUpdates: Partial<LoggedInUserInfo> = {
        isInitiallyDeveloper: false,
      };

      onMembershipUpdate(membershipUpdates);
      onError('Failed to check user permissions. Please refresh the page.');
      // Intentionally mark as complete to allow app to continue with restricted access
      // This prevents the app from being stuck in a loading state due to API failures
      setIsMembershipCheckComplete(true);
      setIsCriticalDataLoaded(true);
    } finally {
      isCheckingRef.current = false;
    }
  }, [
    userAlias,
    isAuthInitialized,
    isServicesInitialized,
    isMembershipCheckComplete,
    hasAttemptedHierarchyMinimalFetch,
    hasAttemptedReportsConfigFetch,
    onMembershipUpdate,
    onError,
    dispatch,
  ]);

  // Reset membership check when user changes
  useEffect(() => {
    if (!userAlias) {
      resetMembershipCheck();
    }
  }, [userAlias, resetMembershipCheck]);

  // Auto-check membership when conditions are met
  useEffect(() => {
    void checkMembership();
  }, [checkMembership]);

  return {
    isMembershipCheckComplete,
    isCriticalDataLoaded,
    checkMembership,
    resetMembershipCheck,
  };
};
